import React from "react";
import styled from "@emotion/styled";
// eslint-disable-next-line no-restricted-imports
import { Table } from "antd5";
import { ColumnType } from "antd5/es/table";
import { SkeletonProps } from "antd5/lib/skeleton";

import SkeletonText from "components/ui/skeleton/SkeletonText";
import { TypedColumnProps } from "../search/SearchTable";

// Varied so a column of placeholders reads as text of different lengths, not a solid block.
const CELL_WIDTHS = ["72%", "56%", "64%", "48%", "68%"];

/** Placeholder rows for a table that is still loading: only a key, which the skeleton columns ignore. */
type SkeletonTableRow = {
  key: string;
};

function skeletonTableRows(count: number): SkeletonTableRow[] {
  return [...Array(count)].map((_, index) => ({ key: `skeleton-row-${index}` }));
}

// Usage:
// <SkeletonTable loading={loading} columns={columns} active={active} rowCount={rowCount}... >
//   <Table columns={columns} ... /> regular table
// </SkeletonTable>
//
// OR
// <SkeletonTable columns={columns} active={active} rowCount={rowCount}... />
//

export function convertTableColumnsToSkeletonColumns<T>(
  columns: ColumnType<T>[],
  active = false,
  className?: string,
): Omit<
  ColumnType<SkeletonTableRow>,
  | "render"
  | "onFilter"
  | "sorter"
  | "children"
  | "onCellClick"
  | "onCell"
  | "onHeaderCell"
  | "shouldCellUpdate"
>[] {
  // Transform columns for SkeletonTable
  return columns.map((col, columnIndex) => {
    let simpleTitle: React.ReactNode;
    if (typeof col.title === "function") {
      if (typeof col.dataIndex === "string") {
        simpleTitle = col.dataIndex;
      } else if (Array.isArray(col.dataIndex)) {
        simpleTitle = (col.dataIndex as string[]).join(".");
      } else if (col.key) {
        simpleTitle = col.key.toString();
      } else {
        simpleTitle = " "; // Default placeholder
      }
    } else {
      simpleTitle = col.title;
    }

    const skeletonColumn = {
      title: simpleTitle,
      key: col.key,
      dataIndex: col.dataIndex as ColumnType<SkeletonTableRow>["dataIndex"],
      width: col.width,
      align: col.align,
      fixed: col.fixed,
      className: col.className,
      ellipsis: col.ellipsis,
      render: function renderPlaceholder(_: unknown, __: SkeletonTableRow, rowIndex: number) {
        return (
          <SkeletonTableCell
            key={col.key}
            align={col.align}
            seed={rowIndex + columnIndex}
            shimmer={active}
            className={className}
          />
        );
      },
    };
    return skeletonColumn;
  });
}

type ColumnAlign = ColumnType<SkeletonTableRow>["align"];

type SkeletonTableCellProps = {
  align?: ColumnAlign;
  /** Picks the bar's width, so neighbouring cells differ. */
  seed: number;
  shimmer?: boolean;
  className?: string;
};

function SkeletonTableCell({ align, seed, shimmer = true, className }: SkeletonTableCellProps) {
  return (
    <SkeletonCell align={align} className={className}>
      <SkeletonText width={CELL_WIDTHS[seed % CELL_WIDTHS.length]} shimmer={shimmer} />
    </SkeletonCell>
  );
}

type SkeletonTableBodyProps = {
  /** Passed in by the table, so the rows pick up its cell padding. */
  className?: string;
  /** One entry per column; null leaves the cell blank, as for a selection column. */
  columnAligns: (ColumnAlign | null)[];
  rowCount: number;
};

/** Placeholder rows to drop in as a table's body while its first rows load. */
export function SkeletonTableBody({ className, columnAligns, rowCount }: SkeletonTableBodyProps) {
  return (
    <tbody className={className}>
      {[...Array(rowCount)].map((_, rowIndex) => (
        <tr key={rowIndex}>
          {columnAligns.map((align, columnIndex) => (
            <td key={columnIndex}>
              {align !== null && <SkeletonTableCell align={align} seed={rowIndex + columnIndex} />}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

const SkeletonCell = styled.div<{ align?: ColumnAlign }>(({ align }) => ({
  display: "flex",
  justifyContent: align === "right" ? "flex-end" : "flex-start",
}));

type SkeletonTableProps<T> = SkeletonProps & {
  columns: ColumnType<T>[] | TypedColumnProps<T>[];
  pageSize: number;
  rowCount?: number;
};

export default function SkeletonTable<T>({
  loading = false,
  active = false,
  rowCount,
  pageSize,
  columns,
  children,
  className,
}: SkeletonTableProps<T>): JSX.Element {
  const skeletonColumns = convertTableColumnsToSkeletonColumns(columns, active, className);
  const rows = rowCount ? Math.min(rowCount, pageSize) : pageSize;
  return loading || !children ? (
    <Table
      rowKey="key"
      pagination={false}
      dataSource={skeletonTableRows(rows)}
      columns={skeletonColumns}
    />
  ) : (
    <>{children}</>
  );
}
